/** Pure labels + Tailwind classes for screening statuses and qualification results. */
import type { Qualification } from '@/features/pipeline/types';
import type { ScreeningStatus } from './types';

export interface StatusMeta {
  label: string;
  /** Chip classes — readable text only (warning uses `text-warning-text`). */
  chip: string;
  /** One-line explanation of what the status means for the recruiter. */
  hint: string;
}

export function screeningStatusMeta(status: ScreeningStatus): StatusMeta {
  switch (status) {
    case 'scheduled':
      return { label: 'Scheduled', chip: 'bg-info-blue-bg text-info-blue', hint: 'The call is queued and will be placed during contact hours.' };
    case 'dialing':
      return { label: 'Dialing', chip: 'bg-info-blue-bg text-info-blue', hint: 'The candidate’s phone is ringing.' };
    case 'in_progress':
      return { label: 'On the call', chip: 'bg-info-blue-bg text-info-blue', hint: 'The recruiting assistant is talking with the candidate now.' };
    case 'completed':
      return { label: 'Completed', chip: 'bg-success-bg text-success', hint: 'Answers were captured and qualification ran.' };
    case 'no_answer':
      return { label: 'No answer', chip: 'bg-warning-bg text-warning-text', hint: 'Nobody picked up. A retry is scheduled if attempts remain.' };
    case 'voicemail':
      return { label: 'Voicemail', chip: 'bg-warning-bg text-warning-text', hint: 'The call went to voicemail. A retry is scheduled if attempts remain.' };
    case 'hung_up':
      return { label: 'Hung up', chip: 'bg-warning-bg text-warning-text', hint: 'The candidate ended the call early. It retries once.' };
    case 'failed':
      return { label: 'Failed', chip: 'bg-danger-bg text-danger', hint: 'The call could not be placed. See the disconnect reason.' };
  }
}

export interface QualificationMeta {
  label: string;
  /** Verdict word colour (readable on white). */
  text: string;
  dot: string;
  ring: string;
  hint: string;
}

export function qualificationResultMeta(q: Qualification): QualificationMeta {
  switch (q) {
    case 'qualified':
      return { label: 'Qualified', text: 'text-success', dot: 'bg-success', ring: 'border-success/40', hint: 'The answers meet this role’s rules. Schedule the interview.' };
    case 'needs_review':
      return { label: 'Needs review', text: 'text-warning-text', dot: 'bg-warning', ring: 'border-warning/40', hint: 'The rules could not settle it. Read the answers and decide.' };
    case 'not_qualified':
      return { label: 'Not qualified', text: 'text-danger', dot: 'bg-danger', ring: 'border-danger/40', hint: 'A knockout or a low score. You can still move the candidate on.' };
    default:
      return { label: 'Not screened', text: 'text-muted', dot: 'bg-gray-400', ring: 'border-gray-200', hint: 'No screening call has produced answers yet.' };
  }
}

/** `95` → `1 min 35 s`; `40` → `40 s`. */
export function durationLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return rest ? `${m} min ${rest} s` : `${m} min`;
}

/** Accessible name for the card's phone glyph; null when there is no active call. */
export function screeningGlyphLabel(status: ScreeningStatus | null | undefined): string | null {
  switch (status) {
    case 'scheduled':
      return 'Screening call scheduled';
    case 'dialing':
      return 'Screening call dialing';
    case 'in_progress':
      return 'Screening call in progress';
    default:
      return null;
  }
}

/** Renders an extracted answer for the table; `—` for missing. */
export function answerLabel(v: unknown): string {
  if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map((x) => answerLabel(x)).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
